import { Competency_Map_Status, CompetencyMapQuery } from "@generated/graphql";
import { LoadingButton } from "@mui/lab";
import { Grid, Stack, Tooltip } from "@mui/material";
import ButtonWithPermissions, { generateUnavailableText } from "components/buttons/ButtonWithPermissions";
import Select from "components/inputs/Select";
import { COMPETENCY_MAP_STATUS_TITLE, COMPETENCY_MAP_STATUSES } from "constants/global";
import { CompanyContext } from "context/companyContext";
import useCreateTesting from "hooks/useCreateTesting";
import { Dispatch, SetStateAction, useContext } from "react";
import { useFormContext } from "react-hook-form";
import { ConstructorTabContext } from "../../../context";
import { CompetencyMapInputs } from "../../types";
import useCompetencyMapController from "./useController";

const COMPETENCY_MAP_STATUSES_FOR_SELECTION = COMPETENCY_MAP_STATUSES.filter(
  ({ value }) => value !== Competency_Map_Status.Generating
);

interface ControlsProps {
  competencyMap: CompetencyMapQuery["competencyMap"];
  setIsEditable: Dispatch<SetStateAction<boolean>>;
  disabled: boolean;
  isCreate: boolean;
}

const Controls = ({ competencyMap, setIsEditable, disabled, isCreate }: ControlsProps) => {
  const { createTesting } = useCreateTesting({ competencyMap });
  const { isCreateAvailable, currentCompany } = useContext(CompanyContext);
  const { selectionMode } = useContext(ConstructorTabContext);
  const { handleSubmit } = useFormContext<CompetencyMapInputs>();
  const { onSubmit, onError, loading, handleChangeStatus } = useCompetencyMapController({
    competencyMap,
    isCreate,
    setIsEditable,
  });

  const createAssessmentUnavailableText = generateUnavailableText(
    isCreateAvailable,
    currentCompany,
    "создания тестирования"
  );

  const status = competencyMap?.status;
  const isExaminationCreationDisabled = status !== Competency_Map_Status.Approved;
  const isCreateTestingDisabled = selectionMode || isExaminationCreationDisabled;
  let createTestingTooltip = "Создание тестирование по всей карте";

  if (selectionMode) {
    createTestingTooltip = "Выйдите из режима выбора или используйте кнопку «Создать тестирование» в панели ниже.";
  } else if (isExaminationCreationDisabled) {
    createTestingTooltip = `Создание тестирования доступно только в статусе «${COMPETENCY_MAP_STATUS_TITLE.APPROVED}»`;
  } else if (createAssessmentUnavailableText) {
    createTestingTooltip = createAssessmentUnavailableText;
  }

  return (
    <Stack
      spacing={3}
      alignItems='flex-end'
      justifyContent='space-between'
      sx={{ width: { xs: "100%", md: 350, lg: 450 } }}>
      <Grid
        container
        justifyContent='end'
        columnSpacing={1}
        rowGap={1}>
        {!isCreate && (
          <Grid
            item
            xs={6}
            md={12}
            lg={6}>
            <Select
              fullWidth
              items={
                status === Competency_Map_Status.Generating
                  ? COMPETENCY_MAP_STATUSES
                  : COMPETENCY_MAP_STATUSES_FOR_SELECTION
              }
              value={status || undefined}
              onChange={handleChangeStatus}
              disabled={
                disabled ||
                competencyMap?.status === Competency_Map_Status.Archive ||
                competencyMap?.status === Competency_Map_Status.Generating
              }
              inputProps={{
                sx: { paddingLeft: 4, py: 0.4 },
              }}
            />
          </Grid>
        )}
        <Grid
          item
          xs={6}
          md={12}
          lg={6}>
          <Tooltip title={createTestingTooltip}>
            <span>
              <ButtonWithPermissions
                buttonText='Создать тестирование'
                onClick={() => createTesting()}
                size='small'
                tooltipText=''
                disabled={isCreateTestingDisabled}
                sx={{ width: "100%" }}
              />
            </span>
          </Tooltip>
        </Grid>
      </Grid>
      {isCreate && (
        <LoadingButton
          variant='contained'
          color='primary'
          onClick={handleSubmit(onSubmit, onError)}
          loading={loading}>
          Сохранить
        </LoadingButton>
      )}
    </Stack>
  );
};

export default Controls;
